import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, BedDouble, Sparkles, Star, Users, ClipboardList, HeartHandshake, Package, MessagesSquare, Brush, BellRing, Gauge, CalendarPlus, Contact, History, Settings, BarChart3, Contact2 } from "lucide-react";
import { ReportsPanel } from "../components/homestay/ReportsPanel";
import { LeadsPanel } from "../components/homestay/LeadsPanel";
import { FlatmateListCard } from "../components/homestay/FlatmateListCard";
import { useAuth } from "../context/AuthContext";
import { RegisterPropertyForm, StatusCard, LockedCard } from "../components/homestay/OwnerGate";
import { BedBoard } from "../components/homestay/BedBoard";
import { CommandCentre } from "../components/homestay/CommandCentre";
import { BRAND } from "../components/homestay/constants";
import { ManagersPanel } from "../components/homestay/ManagersPanel";
import { SummaryPanel } from "../components/homestay/SummaryPanel";
import { BookingsPanel } from "../components/homestay/BookingsPanel";
import { LedgerPanel } from "../components/homestay/LedgerPanel";
import { RequestsPanel } from "../components/homestay/RequestsPanel";
import { ComplaintsPanel } from "../components/homestay/ComplaintsPanel";
import { InventoryPanel } from "../components/homestay/InventoryPanel";
import { KitPanel } from "../components/homestay/KitPanel";
import { ChatPanel } from "../components/homestay/ChatPanel";
import { ReviewsPanel } from "../components/homestay/ReviewsPanel";
import { BrandingPanel, BrandMark } from "../components/homestay/BrandingPanel";
import { TasksPanel } from "../components/homestay/TasksPanel";
import { RemindersPanel } from "../components/homestay/RemindersPanel";
import { NewBookingPanel } from "../components/homestay/NewBookingPanel";
import { GuestsPanel } from "../components/homestay/GuestsPanel";
import { ActivityPanel } from "../components/homestay/ActivityPanel";
import { SettingsPanel } from "../components/homestay/SettingsPanel";
import { OverviewPanel, ExtendAssistPanel } from "../components/homestay/OverviewPanel";

const CARE = ["care", "Requests & Care", HeartHandshake, "nav-care"];
const INV = ["inventory", "Inventory", Package, "nav-inventory"];
const CHAT = ["chat", "Chat", MessagesSquare, "nav-chat"];
const TASKS = ["tasks", "Housekeeping", Brush, "nav-tasks"];
const REMIND = ["reminders", "Reminders", BellRing, "nav-reminders"];
const OVERVIEW = ["overview", "Overview", Gauge, "nav-overview"];
const BEDS = ["beds", "Bed Board", BedDouble, "nav-bed-board"];
const BOOK = ["book", "New Booking", CalendarPlus, "nav-new-booking"];
const GUESTS = ["guests", "Guests", Contact, "nav-guests"];
const ACTIVITY = ["activity", "Activity Log", History, "nav-activity"];
const REPORTS = ["reports", "Reports", BarChart3, "nav-reports"];
const LEADS = ["leads", "Leads", Contact2, "nav-leads"];
const OWNER_TABS = [["", "Command Centre", LayoutDashboard, "nav-command-centre"], OVERVIEW, BEDS, BOOK, GUESTS, CHAT, CARE, TASKS, REMIND, INV, REPORTS, LEADS, ACTIVITY, ["settings", "Property Settings", Settings, "nav-settings"], ["team", "Team & Brand", Users, "nav-team"]];
const MANAGER_TABS = [OVERVIEW, BEDS, BOOK, ["ops", "Bookings & Payments", ClipboardList, "nav-ops"], GUESTS, CHAT, CARE, TASKS, REMIND, INV, REPORTS, LEADS, ACTIVITY];

function SharedBody({ tab, property, ...props }) {
  if (tab === "overview") return <OverviewPanel {...props} />;
  if (tab === "reports") return <ReportsPanel {...props} />;
  if (tab === "leads") return <LeadsPanel {...props} />;
  if (tab === "book") return <NewBookingPanel {...props} property={property} />;
  if (tab === "guests") return <GuestsPanel {...props} />;
  if (tab === "activity") return <ActivityPanel {...props} />;
  return null;
}

function CareBody({ property, isOwner, ...props }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="care-view">
      <div className="lg:col-span-2"><ExtendAssistPanel {...props} /></div>
      <RequestsPanel {...props} />
      <ComplaintsPanel {...props} />
      <div className="lg:col-span-2"><ReviewsPanel {...props} isOwner={isOwner} /></div>
      {isOwner && <div className="lg:col-span-2"><KitPanel property={property} authFetch={props.authFetch} API={props.API} /></div>}
    </div>
  );
}

function StaffChat({ pid, authFetch, API, property, userId }) {
  return <ChatPanel threadsUrl={`${API}/homestay/properties/${pid}/threads`} me={property.my_role} userId={userId} authFetch={authFetch} API={API} />;
}

function OwnerBody({ tab, pid, authFetch, API, property, userId, reload }) {
  const props = { pid, authFetch, API };
  const shared = SharedBody({ tab, property, ...props });
  if (shared) return shared;
  if (tab === "settings") return <div className="space-y-4"><FlatmateListCard property={property} authFetch={authFetch} API={API} onSaved={reload} /><SettingsPanel property={property} authFetch={authFetch} API={API} onSaved={reload} /></div>;
  if (tab === "tasks") return <TasksPanel {...props} />;
  if (tab === "reminders") return <RemindersPanel {...props} />;
  if (tab === "chat") return <StaffChat {...props} property={property} userId={userId} />;
  if (tab === "beds") return <BedBoard {...props} />;
  if (tab === "care") return <CareBody property={property} isOwner {...props} />;
  if (tab === "inventory") return <InventoryPanel {...props} isOwner />;
  if (tab === "team") return <div className="grid lg:grid-cols-2 gap-4"><BrandingPanel property={property} authFetch={authFetch} API={API} onSaved={reload} /><ManagersPanel {...props} /><SummaryPanel {...props} /></div>;
  return <CommandCentre {...props} />;
}

function ManagerBody({ tab, pid, authFetch, API, property, userId }) {
  const props = { pid, authFetch, API };
  const shared = SharedBody({ tab, property, ...props });
  if (shared) return shared;
  if (tab === "tasks") return <TasksPanel {...props} />;
  if (tab === "reminders") return <RemindersPanel {...props} />;
  if (tab === "chat") return <StaffChat {...props} property={property} userId={userId} />;
  if (tab === "beds") return <BedBoard {...props} />;
  if (tab === "care") return <CareBody property={property} isOwner={false} {...props} />;
  if (tab === "inventory") return <InventoryPanel {...props} isOwner={false} />;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="manager-ops">
      <BookingsPanel {...props} onChange={() => {}} />
      <LedgerPanel {...props} onChange={() => {}} canDelete={false} />
      <div className="lg:col-span-2"><SummaryPanel {...props} /></div>
    </div>
  );
}

function AccessBadge({ access }) {
  if (access.featured) return <span data-testid="hs-badge-featured" className="rounded-full px-3 py-1 text-[11px] font-bold text-white inline-flex items-center gap-1" style={{ background: BRAND.orange }}><Star className="w-3 h-3" /> FEATURED</span>;
  if (access.in_trial) return <span data-testid="hs-badge-trial" className="rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1 border-2" style={{ borderColor: BRAND.orange, color: BRAND.orange }}><Sparkles className="w-3 h-3" /> Trial · {access.trial_days_left} days left</span>;
  return null;
}

function OwnerNav({ tabs, tab, alerts, unread, empty }) {
  return (
    <div className="inline-flex flex-wrap rounded-full border-2 p-1 bg-white" style={{ borderColor: BRAND.line }}>
      {tabs.map(([key, l, I, tid]) => { const active = key === tab; const to = key ? `/homestay/owner/${key}` : "/homestay/owner"; return (
        <Link key={tid} to={to} data-testid={tid} className="px-4 py-2 rounded-full text-sm font-bold inline-flex items-center gap-1.5 transition-colors"
          style={{ background: active ? BRAND.green : "transparent", color: active ? "#fff" : BRAND.green }}>
          <I className="w-4 h-4" /> {l}
          {key === "beds" && empty > 0 && <span data-testid="nav-empty-beds" className="bed-blink ml-1 rounded-full px-1.5 text-[10px] font-bold text-white" style={{ background: "#FF8C1A" }}>{empty}</span>}
          {key === "chat" && unread > 0 && <span data-testid="nav-chat-unread" className="bed-blink ml-1 rounded-full px-1.5 text-[10px] font-bold text-white" style={{ background: BRAND.orange }}>{unread}</span>}
          {key === "care" && alerts > 0 && <span data-testid="nav-care-new" className="bed-blink ml-1 rounded-full px-1.5 text-[10px] font-bold text-white" style={{ background: BRAND.orange }}>{alerts} NEW</span>}
        </Link>
      ); })}
    </div>
  );
}

export default function HomeStayOwner() {
  const { user, loading, authFetch, API } = useAuth();
  const loc = useLocation();
  const seg = loc.pathname.split("/homestay/owner")[1]?.replace(/^\//, "") || "";
  const [props, setProps] = useState(null);

  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/mine` }).then((r) => setProps(r.data.items)).catch(() => setProps([])), [authFetch, API]);
  useEffect(() => { if (user) load(); }, [user, load]);
  const [alerts, setAlerts] = useState(0);
  const pid0 = props?.[0]?.access?.unlocked ? props[0].id : null;
  useEffect(() => {
    if (!pid0) return;
    Promise.all([authFetch({ url: `${API}/homestay/properties/${pid0}/requests` }), authFetch({ url: `${API}/homestay/properties/${pid0}/complaints` })])
      .then(([a, b]) => setAlerts(a.data.pending + b.data.open)).catch(() => {});
  }, [pid0, authFetch, API, loc.pathname]);
  const [unread, setUnread] = useState(0);
  const [empty, setEmpty] = useState(0);
  useEffect(() => {
    if (pid0) authFetch({ url: `${API}/homestay/properties/${pid0}/overview` }).then((r) => setEmpty(r.data.empty_beds)).catch(() => {});
  }, [pid0, authFetch, API, loc.pathname]);
  useEffect(() => {
    if (!pid0) return;
    const f = () => authFetch({ url: `${API}/homestay/properties/${pid0}/threads` }).then((r) => setUnread(r.data.unread_total)).catch(() => {});
    f(); const t = setInterval(f, 15000); return () => clearInterval(t);
  }, [pid0, authFetch, API, loc.pathname]);

  if (loading) return null;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (!props) return <div className="max-w-6xl mx-auto px-4 py-10 text-sm" style={{ color: BRAND.muted }}>Loading…</div>;

  const p = props[0];
  let body;
  if (!p) body = <RegisterPropertyForm authFetch={authFetch} API={API} onCreated={load} />;
  else if (!p.access.approved) body = <StatusCard property={p} />;
  else if (!p.access.unlocked) body = <LockedCard property={p} />;
  const isMgr = p?.my_role === "manager";
  const tabs = isMgr ? MANAGER_TABS : OWNER_TABS;
  const tab = tabs.some(([k]) => k === seg) ? seg : tabs[0][0];
  if (p && p.access.unlocked) body = isMgr ? <ManagerBody tab={tab} pid={p.id} property={p} userId={user.id} authFetch={authFetch} API={API} /> : <OwnerBody tab={tab} pid={p.id} property={p} userId={user.id} authFetch={authFetch} API={API} reload={load} />;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-24" data-testid="homestay-owner-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          {p && <BrandMark property={p} size={56} tid="hs-brand-logo" />}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.orange }} data-testid="hs-role-label">HomeStay Feel · {isMgr ? "Manager" : "Owner"}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold truncate" style={{ color: BRAND.green }} data-testid="hs-property-name">{p ? p.brand_name || p.name : "Owner Command Centre"}</h1>
            {p?.tagline && <div className="text-sm" style={{ color: BRAND.muted }} data-testid="hs-brand-tagline">{p.tagline}</div>}
            {p && <div className="mt-1 flex items-center gap-2 text-xs" style={{ color: BRAND.muted }}>{p.locality}, {p.city} <AccessBadge access={p.access} /></div>}
          </div>
        </div>
        {p?.access.unlocked && <OwnerNav tabs={tabs} tab={tab} alerts={alerts} unread={unread} empty={empty} />}
      </div>
      {body}
    </div>
  );
}
